import {
  Box,
  Button,
  Card,
  Container,
  Flex,
  Link,
  Text,
} from '@radix-ui/themes';
import { CommonCallout } from 'components/common/callouts';
import { BetaIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import { InstallationContext } from 'components/machine/dialogs/installation/context';
import { CommonStepHeader } from 'components/machine/dialogs/installation/header';
import { MachineContext } from 'contexts/machine.context';
import {
  isTrackingDeviceBeta,
  isTrackingDeviceEnabled,
  isTrackingDeviceVisible,
} from 'enums/beta';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import {
  MachineHelper,
  getDistanceOptions,
} from 'lib_ts/classes/machine.helper';
import { BallType, CalibrateProc } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'StepGeneralConfig';

export const StepGeneralConfig = () => {
  const current = useAuthStore((s) => s.current);

  const { readOnly, installation, syncingBallType, syncBallType } =
    useContext(InstallationContext);
  const {
    machine,
    isActive,
    calibrate,
    logPlateDistanceChange,
    update: updateMachine,
  } = useContext(MachineContext);

  const [moundToggled, setMoundToggled] = useState(false);
  const disableMoundOnUnmount = useRef(moundToggled);

  const { last_installation } = machine;

  // should we just defer saving until the user moves to next step?
  const saveChanges = async (value: Partial<IMachine>) => {
    try {
      // await so that loading doesn't revert to false too soon
      await updateMachine({
        ...value,
        _id: machine._id,
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    // turn off the mound display when we leave this step
    return () => {
      if (disableMoundOnUnmount.current) {
        calibrate([CalibrateProc.ProjectorMound], COMPONENT_NAME);
      }
    };
  }, []);

  useEffect(() => {
    if (machine.tracking_device !== TrackingDevice.TrackmanB1 && moundToggled) {
      calibrate([CalibrateProc.ProjectorMound], COMPONENT_NAME);
      setMoundToggled(false);
    }
  }, [machine.tracking_device, moundToggled]);

  const deviceOptions = useMemo(
    () =>
      Object.values(TrackingDevice).map((m) => {
        const o: IOptionExt = {
          label: m,
          value: m,
          invisible: !isTrackingDeviceVisible(m, machine),
          disabled: !isTrackingDeviceEnabled(m, machine),
          suffix: isTrackingDeviceBeta(m) ? <BetaIcon /> : undefined,
        };

        return o;
      }),
    [machine]
  );

  // only allowed ball types (for this machine) can be selected since a baseline model is still required
  const ballTypeOptions = useMemo(
    () =>
      (machine.ball_type_options ?? [BallType.MLB]).map((t) => ({
        label: t,
        value: t,
      })),
    [machine.ball_type_options]
  );

  const insetFt = MachineHelper.getWheelsInsetFt(machine.hardware_version);

  const plateDistanceFt = machine.releaseFt - insetFt;

  // b/c the input is expecting a string value for the plate distance, rounded to the nearest inch
  const plateDistanceValue = useMemo(
    () => MachineHelper.getDistanceOptionValue(plateDistanceFt)?.value,
    [plateDistanceFt]
  );

  const distanceOptions = useMemo(
    () => getDistanceOptions(plateDistanceFt),
    [plateDistanceFt]
  );

  if (!last_installation) {
    return <CommonCallout text="No installation definition!" />;
  }

  const ballTypeLocked = installation.ball_type_locked;

  return (
    <ErrorBoundary identifier="StepGeneralConfig">
      <Container size="2">
        <Flex direction="column" gap="4" px={{ sm: '0', md: '6', lg: '9' }}>
          <CommonStepHeader label="General Configuration" />

          <CommonFormGrid columns={1}>
            <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
              <CommonSelectInput
                id="machine-plate-distance"
                label="settings.plate-distance"
                disabled={readOnly}
                options={distanceOptions}
                value={plateDistanceValue}
                onNumericChange={(nextPlateDistanceFt) => {
                  if (nextPlateDistanceFt === plateDistanceFt) {
                    // do nothing to avoid overwriting prev value unnecessarily
                    return;
                  }

                  const prevPlateDistanceFt = machine.releaseFt - insetFt;

                  logPlateDistanceChange({
                    prevPlateFt: prevPlateDistanceFt,
                    nextPlateFt: nextPlateDistanceFt,
                  });

                  saveChanges({
                    releaseFt: nextPlateDistanceFt + insetFt,
                    last_plate_distance_ft: prevPlateDistanceFt,
                    last_plate_distance_changed: new Date().toISOString(),
                    last_plate_distance_changedBy: current.email,
                  });
                }}
                skipSort
                suppressWarning
              />
              <Text color={RADIX.COLOR.SECONDARY}>
                {t('settings.tape-measured-distance-msg')}
              </Text>
            </Flex>
          </CommonFormGrid>
          <CommonFormGrid columns={1}>
            <Box>
              <CommonSelectInput
                id="machine-tracking-device"
                name="machine-tracking-device"
                label="common.tracking-device"
                disabled={readOnly}
                options={deviceOptions}
                value={machine.tracking_device}
                onChange={(v) => {
                  const safeValue = v as TrackingDevice;

                  saveChanges({
                    tracking_device: safeValue,
                  });
                }}
              />
            </Box>
          </CommonFormGrid>
          <CommonFormGrid columns={2}>
            {machine.tracking_device === TrackingDevice.RapsodoV3PRO && (
              <Box gridColumn="span 2">
                <CommonTextInput
                  id="machine-rapsodo-serial"
                  name="machine-rapsodo-serial"
                  label="common.rapsodo-serial"
                  disabled={readOnly || machine.sandbox}
                  value={machine.rapsodo_serial}
                  debounceMs={1_000}
                  onChange={(v) => {
                    const safeValue = (v ?? '').trim();
                    saveChanges({ rapsodo_serial: safeValue });
                  }}
                  monospace
                />
              </Box>
            )}
            {machine.tracking_device === TrackingDevice.TrackmanB1 && (
              <>
                <Box>
                  <CommonTextInput
                    id="machine-trackman-first-name"
                    label="settings.trackman-first-name-string"
                    value={machine.machineID}
                    monospace
                    disabled
                  />
                </Box>
                <Box>
                  <CommonTextInput
                    id="machine-trackman-last-name"
                    label="settings.trackman-last-name-string"
                    value={machine._id.slice(-4)}
                    monospace
                    disabled
                  />
                </Box>
              </>
            )}

            {machine.tracking_device === TrackingDevice.TrackmanB1 && (
              <Box gridColumn="span 2">
                <Card variant="classic" className="transparent-card-bg">
                  <Flex gap={RADIX.FLEX.GAP.MD} align="center">
                    <Flex direction="column">
                      <Text weight="medium">Calibrate Trackman</Text>
                      <Text color={RADIX.COLOR.SECONDARY}>
                        Enable this option to display the mound view on the Arc
                        for accurate{' '}
                        <Link
                          href="https://intercom.help/trajekt-sports/en/articles/9892036-trackman-tracking-device-setup"
                          target="_blank"
                          rel="noreferrer"
                          color={RADIX.COLOR.ACCENT}
                        >
                          Trackman calibration
                        </Link>
                        .
                      </Text>
                    </Flex>
                    <Button
                      disabled={!isActive}
                      variant={moundToggled ? 'solid' : 'soft'}
                      onClick={() => {
                        calibrate(
                          [CalibrateProc.ProjectorMound],
                          COMPONENT_NAME
                        );

                        if (!moundToggled) {
                          setMoundToggled(true);
                          disableMoundOnUnmount.current = true;
                          return;
                        }

                        setMoundToggled(false);
                        disableMoundOnUnmount.current = false;
                      }}
                    >
                      {moundToggled ? 'Hide Mound' : 'Display Mound'}
                    </Button>
                  </Flex>
                </Card>
              </Box>
            )}
            {ballTypeLocked && (
              <Box gridColumn="span 2">
                <CommonCallout
                  color={RADIX.COLOR.WARNING}
                  text="Ball type cannot be changed after starting Ball Type Calibration or Repeatability Testing. Cancel this installation and start over to select a different ball type."
                />
              </Box>
            )}
            <Box gridColumn="span 2">
              <CommonSelectInput
                id="machine-ball-type"
                name="machine-ball-type"
                label="common.ball-type"
                disabled={readOnly || ballTypeLocked || syncingBallType}
                options={ballTypeOptions}
                value={machine.ball_type}
                onChange={(v) => {
                  syncBallType(v as BallType);
                }}
              />
            </Box>
          </CommonFormGrid>
        </Flex>
      </Container>
    </ErrorBoundary>
  );
};
